import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_POSITION_STATUS } from "../graphql/validation";
import { useLogger } from "../services/logging";
import "./TradeReopeningWorkflow.css";

interface TradeReopeningWorkflowProps {
  tradeId: number;
  onReopen: () => Promise<void>;
  onCancel: () => void;
  onClose?: () => void;
  className?: string;
}

interface PositionToReopen {
  id: string;
  type: string;
  quantity: number;
  price: number;
  currentStatus: string;
  newStatus: string;
}

const TradeReopeningWorkflow: React.FC<TradeReopeningWorkflowProps> = ({
  tradeId,
  onReopen,
  onCancel,
  onClose,
  className = "",
}) => {
  const logger = useLogger("TradeReopeningWorkflow");
  const [step, setStep] = useState(1);
  const [positions, setPositions] = useState<PositionToReopen[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // GraphQL mutation for updating position status
  const [updatePositionStatusMutation] = useMutation(UPDATE_POSITION_STATUS);

  const totalSteps = 3;

  const handlePositionStatusChange = (
    positionId: string,
    newStatus: string,
  ) => {
    setPositions((prev) =>
      prev.map((pos) =>
        pos.id === positionId ? { ...pos, newStatus: newStatus } : pos,
      ),
    );
  };

  const handleReopen = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Update position statuses first
      for (const position of positions) {
        if (position.currentStatus !== position.newStatus) {
          await updatePositionStatus(position.id, position.newStatus);
        }
      }

      // Then reopen the trade
      await onReopen();

      // Move to success step
      setStep(4);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Failed to reopen trade",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updatePositionStatus = async (positionId: string, status: string) => {
    try {
      // Use GraphQL mutation for updating position status
      const result = await updatePositionStatusMutation({
        variables: {
          id: positionId,
          status: status,
        },
      });

      if (!result.data?.updatePosition) {
        throw new Error("Failed to update position status");
      }
    } catch (error) {
      logger.error(
        "GraphQL mutation error",
        { method: "handleReopenTrade" },
        error,
      );
      throw new Error("Failed to update position status");
    }
  };

  const canProceed =
    step === 1 ||
    (step === 2 && positions.length > 0) ||
    (step === 3 && positions.every((p) => p.newStatus === "OPEN"));

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="workflow-step">
            <h3>Step 1: Review Trade Status</h3>
            <p>This trade is currently closed. To reopen it, you'll need to:</p>
            <ul>
              <li>Review the current positions</li>
              <li>Change position statuses from CLOSED to OPEN</li>
              <li>Confirm the reopening</li>
            </ul>
            <p>
              <strong>Note:</strong> Reopening a trade will allow you to modify
              positions and add new ones.
            </p>
          </div>
        );

      case 2:
        return (
          <div className="workflow-step">
            <h3>Step 2: Select Positions to Reopen</h3>
            <p>Choose which positions should be reopened:</p>

            <div className="positions-list">
              {positions.map((position) => (
                <div key={position.id} className="position-item">
                  <div className="position-info">
                    <span className="position-type">{position.type}</span>
                    <span className="position-details">
                      {position.quantity} @ ${position.price}
                    </span>
                  </div>

                  <div className="position-status">
                    <span className="current-status">
                      Current: {position.currentStatus}
                    </span>
                    <select
                      value={position.newStatus}
                      onChange={(e) =>
                        handlePositionStatusChange(position.id, e.target.value)
                      }
                      className="status-select"
                    >
                      <option value="CLOSED">CLOSED</option>
                      <option value="OPEN">OPEN</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="workflow-step">
            <h3>Step 3: Confirm Reopening</h3>
            <p>Review the changes before reopening the trade:</p>

            <div className="confirmation-summary">
              <h4>Summary of Changes:</h4>
              <ul>
                {positions
                  .filter((p) => p.currentStatus !== p.newStatus)
                  .map((position) => (
                    <li key={position.id}>
                      Position {position.type}: {position.currentStatus} →{" "}
                      {position.newStatus}
                    </li>
                  ))}
              </ul>

              {positions.every((p) => p.currentStatus === p.newStatus) && (
                <p className="no-changes">
                  No changes needed - all positions are already open.
                </p>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="workflow-step success">
            <h3>✅ Trade Successfully Reopened!</h3>
            <p>The trade has been reopened and you can now modify positions.</p>
            <div className="success-actions">
              <button onClick={onClose || onCancel} className="close-button">
                Close
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {Array.from({ length: totalSteps }, (_, index) => (
        <div
          key={index}
          className={`step-dot ${index + 1 <= step ? "active" : ""} ${index + 1 === step ? "current" : ""}`}
        >
          {index + 1}
        </div>
      ))}
    </div>
  );

  const renderActions = () => {
    if (step === 4) return null;

    return (
      <div className="workflow-actions">
        <button
          onClick={onClose || onCancel}
          className="cancel-button"
          disabled={isLoading}
        >
          Cancel
        </button>

        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            className="back-button"
            disabled={isLoading}
          >
            Back
          </button>
        )}

        {step < totalSteps && (
          <button
            onClick={() => setStep(step + 1)}
            className="next-button"
            disabled={!canProceed || isLoading}
          >
            Next
          </button>
        )}

        {step === totalSteps && (
          <button
            onClick={handleReopen}
            className="reopen-button"
            disabled={!canProceed || isLoading}
          >
            {isLoading ? "Reopening..." : "Reopen Trade"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`trade-reopening-workflow ${className}`}>
      <div className="workflow-header">
        <h2>Reopen Trade</h2>
        <p>Trade ID: {tradeId}</p>
      </div>

      {renderStepIndicator()}

      <div className="workflow-content">
        {renderStepContent()}

        {error && <div className="workflow-error">Error: {error}</div>}
      </div>

      {renderActions()}
    </div>
  );
};

export default TradeReopeningWorkflow;
